/**
 * Text helpers for prose and handles built from data. Shared so web and mobile
 * word a sentence, and read a typed handle, the same way.
 */

/**
 * A run of copy. What a sentence turns on is `strong`, so each platform can
 * set it in its own emphasis.
 */
export interface CopySegment {
  text: string;
  strong?: boolean;
  /** A number in the sentence, set in mono so it lines up when it changes */
  figure?: boolean;
}

/**
 * Join phrases the way a sentence would: "a", "a and b", "a, b and c".
 *
 * No serial comma and no em dash separator — both grant bands that use this
 * embed the result in a sentence that already carries a dash, and a second one
 * inside the list makes the clause unreadable.
 *
 * `conjunction` is `or` for a negative clause ("could not read x or y"), where
 * `and` would claim both failed together — a different and weaker statement.
 *
 * Empty input yields an empty string, so a caller that filtered everything out
 * renders nothing rather than a dangling "and".
 */
export function toSentenceList(
  phrases: string[],
  conjunction: 'and' | 'or' = 'and'
): string {
  if (phrases.length <= 1) return phrases[0] ?? '';
  return `${phrases.slice(0, -1).join(', ')} ${conjunction} ${phrases[phrases.length - 1]}`;
}

/**
 * The noun for a count: "session" for 1, "sessions" otherwise. An irregular
 * plural is passed in ("thing", "things" is the default, "foot", "feet" is not).
 *
 * The one place the rule lives — every list, verdict and note that counts
 * something used to carry its own copy, and a copy is where "1 sessions"
 * creeps in.
 */
export function pluralise(
  count: number,
  one: string,
  many = `${one}s`
): string {
  return count === 1 ? one : many;
}

/** The count and its noun together: `3 sessions`, `1 goal`. */
export function countNoun(count: number, one: string, many?: string): string {
  return `${count} ${pluralise(count, one, many)}`;
}

/**
 * A typed or pasted handle as the API matches it: trimmed, without a leading
 * `@`. Friend and trainer requests both match an exact username, so a pasted
 * "@handle" would be refused on a value the user considers correct.
 */
export function cleanUsername(username: string): string {
  return username.trim().replace(/^@/, '');
}
